// V49 — ToolPageBadge.jsx
//
// Standard tool-page header badge: sits between the back arrow and the
// tool name in every *Shell. Lights up the active suite's accent.
//
// DEFAULT (Option B per Ian's V49 lock): per-tool glyph from
// toolGlyphs.jsx, color-keyed to the suite accent. If the tool doesn't
// have a registered glyph, falls back to the family badge automatically.
//
// FALLBACK SWAP (Option A): if at any point we want every tool page to
// show the family's 2-letter badge (Ma/Ch/Bi/Ph/T/G/S) instead of the
// per-tool glyph, swap the default `kind` prop to "family" below — or
// pass <ToolPageBadge kind="family" /> at the call site. Single-line
// change.

import { glyphFor } from "./toolGlyphs.jsx";

// Locked 2-letter family badges per suite. Used when kind === "family"
// or when a tool has no registered glyph.
const FAMILY_BADGES = {
  bio:   { letters: "Bi", deep: "#7C3AED" },
  chem:  { letters: "Ch", deep: "#7C3AED" },
  math:  { letters: "Ma", deep: "#7C3AED" },
  phys:  { letters: "Ph", deep: "#7C3AED" },
  tools: { letters: "T",  deep: "#7C3AED" },
  games: { letters: "G",  deep: "#7C3AED" },
  stack: { letters: "S",  deep: "#7C3AED" },
  music: { letters: "Mu", deep: "#7C3AED" },
  history: { letters: "H", deep: "#7C3AED" },
};

/**
 * <ToolPageBadge name="Quadratic Solver" family="math" accent="#5BB3F8" />
 *
 * Renders a 38×38 square badge:
 *   - kind="glyph" (default) — per-tool glyph from toolGlyphs.jsx,
 *     gradient-filled with the family accent → family violet.
 *     Falls back to family-letters badge if no glyph is registered.
 *   - kind="family" — always renders the 2-letter family badge.
 */
export function ToolPageBadge({ name, family, accent, kind = "glyph" }) {
  const fam = FAMILY_BADGES[family] || { letters: "?", deep: "#7C3AED" };

  // If caller asked for family badge OR no glyph exists for the tool,
  // render the letters badge.
  const glyph = kind === "glyph" ? glyphFor(name) : null;
  const useGlyph = !!glyph;

  const wrap = {
    width: 38, height: 38, borderRadius: 11,
    background: `linear-gradient(135deg, ${accent}, ${fam.deep})`,
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 17, fontWeight: 800, color: "#fff",
    flexShrink: 0,
  };

  if (useGlyph) {
    // Shrink the SVG and re-color for badge background. The glyph is
    // 22×22 default; in the 38×38 badge we render it at 22px with
    // currentColor → white so it reads cleanly on the gradient.
    return (
      <div style={wrap} aria-hidden="true">
        <span style={{ color: "#fff", display: "flex" }}>{glyph}</span>
      </div>
    );
  }
  // Family-letters fallback
  return <div style={wrap}>{fam.letters}</div>;
}
